import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaMountain } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './TourDetail.css';

const Brahmatal = () => {
  const tourData = {
    name: "Brahmatal Trek",
    duration: "05 Nights 06 Days",
    price: "₹ 5,999/-",
    image: "https://res.cloudinary.com/duym2bgp2/image/upload/v1754935197/k4vfrhgmwr5efnqecus8.jpg",
    altitude: "12,100 ft",
    difficulty: "Moderate",
    region: "Uttarakhand",
    highlights: ["Winter trek", "Snow-covered trails", "Mountain views"],
    bestTime: "December to March",
    description: "The Brahmatal Trek is a beautiful winter trek in Uttarakhand that takes you through snow-covered trails and offers stunning views of the Himalayas. This moderate trek is perfect for those looking for a challenging yet rewarding winter adventure in the mountains."
  };

  return (
    <>
      <Helmet>
        <title>Brahmatal Trek - NomadTrailz</title>
        <meta name="description" content="Experience the beautiful Brahmatal Trek in Uttarakhand. Winter trek with snow-covered trails and stunning mountain views." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <section className="tour-hero">
          <div className="hero-background">
            <img
              src={tourData.image}
              alt={tourData.name}
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {tourData.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              A beautiful winter trek with snow-covered trails and stunning mountain views
            </motion.p>
          </div>
        </section>

        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>{tourData.description}</p>
                  <p>
                    This trek takes you through the beautiful Brahmatal region, known for its winter 
                    beauty and snow-covered landscapes. The trek offers spectacular views of peaks 
                    like Nanda Ghunti, Trishul, and other Himalayan giants. It's a perfect choice 
                    for those who want to experience the magic of winter trekking in the Himalayas.
                  </p>
                </motion.div>

                <motion.div
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>Winter trek with snow-covered trails</li>
                    <li>Stunning mountain views</li>
                    <li>Beautiful alpine meadows</li>
                    <li>Moderate difficulty level</li>
                    <li>Perfect for winter adventure</li>
                    <li>Photography opportunities</li>
                    <li>Rhododendron forests</li>
                    <li>High-altitude camping</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: From Rishikesh to Lohajung</h4>
                    <ul>
                      <li>There is a significant religious belief that Lohajung is a location where the goddess Parvati fought a battle against the demon Lohasur.</li>
                      <li>As a result, the location is known as Lohajung, which literally translates to "War with Lohasur."</li>
                      <li>Beginning the journey from Rishikesh to Lohajung will take 10-12 hours.</li>
                      <li>Along the way, you will have the opportunity to take in a number of different mountain panoramas.</li>
                      <li>After you have arrived in Lohajung, you will be able to see the breathtaking Nanda Ghunti mountain.</li>
                      <li>In the vicinity of Lohajung, there are twelve to fifteen more settlements.</li>
                      <li>You may purchase the essential items for winter clothing from the local merchants in the village.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: From Lohajung to Bekaltal</h4>
                    <ul>
                      <li>The Lohajung central market serves as the starting point for the pilgrimage.</li>
                      <li>During the trek, you will first traverse the top part of Lohajung, and then go via the woodland route that has a few cottages.</li>
                      <li>Mandoli is the name of the village that will be reached after a short trek.</li>
                      <li>As you ascend, you will eventually arrive at the Kali valley, which is the point where two rivers, the Kali and the Pindari, meet.</li>
                      <li>Later on in the journey, you will come across rhododendron trees, which are in bloom from February to March.</li>
                      <li>To assist you in crossing the creek, there is a bridge that is ten metres long and is called Link-Gaad.</li>
                      <li>Following a two-hour journey, you will arrive at Begum, where you will be rewarded with breathtaking views of Bagdi Bugyal and Navali Bugyal.</li>
                      <li>From there, it will only take you seventy-five minutes of walking to reach Bekaltal.</li>
                      <li>We may set up camp next to the neighbouring lake known as Khopdaliya, which is located close to the stunning lake known as Bekaltal.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: From Bekaltal to Brahmatal</h4>
                    <ul>
                      <li>As you make your way through the forest today, you will come across oak and rhododendron trees.</li>
                      <li>A hard ascent into the Rhododendron and Oak woodland is the beginning of the trek.</li>
                      <li>After an hour of climbing, the route opens up to the meadows of Telandi, which gets you to the highest point of the day, which is 11,500 feet.</li>
                      <li>You will be able to view the Nainital and Almora side of the valley after you have completed the whole day of walking.</li>
                      <li>Some of the larger mountains in the region, such as Nanda Ghunti and Trishul, are visible to visitors.</li>
                      <li>Brahmatal's campground is located at an elevation of 10,351 feet, and it is 500 metres away from the lake.</li>
                      <li>You will have a stunning view of the area as you set up camp at this peaceful spot.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 4: From Brahmatal to Summit and back</h4>
                    <ul>
                      <li>The journey starts with a 40-minute ascent through oak and rhododendron woods that are mingled together.</li>
                      <li>This leads to wide meadows and the beginning of the steep ascent to the summit.</li>
                      <li>You will be able to view the mountains Mana, Neelkanth, Nanda Ghunti, Chaukhambha, Nilgiri, Mandir, Trishul, and Maitoli.</li>
                      <li>You will also see high mountains that extend all the way to the Panchachuli range.</li>
                      <li>An experience that will be mesmerizing is going to be trekking towards the peak.</li>
                      <li>It will be a long trek, but at every step, you will witness something that you will enjoy in your surroundings.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 5: From Brahmatal to Lohajung</h4>
                    <ul>
                      <li>The descent from Brahmatal to Lohajung takes around 7-8 hours to complete.</li>
                      <li>From there, you make your way back via the path that leads to Khabekhal, then continue straight down to Lohajung.</li>
                      <li>Next, you pass through Daldum, a beautiful area surrounded by forests.</li>
                      <li>A journey of twenty to thirty minutes will bring you to Malling, located outside the woodland region.</li>
                      <li>From this vantage point, you will also be able to glimpse a few settlements.</li>
                      <li>After travelling for an hour, you will arrive at Burali, which is a tiny village consisting of a few separate dwellings.</li>
                      <li>Once again, you will discover a stone path that leads in the direction of Lohajung.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 6: From Lohajung to Rishikesh</h4>
                    <ul>
                      <li>In the morning after having our breakfast we will proceed to visit Rishikesh.</li>
                      <li>It will take one 10-12 hours to get to Rishikesh on the trail.</li>
                      <li>Therefore I guaranteed you will have enough time or even more than enough to look at the beautiful scenery.</li>
                      <li>This is the final day of your trek, so mainly have fun while traveling and try to create beautiful moments.</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div
                  className="things-to-carry-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Things To Carry</h3>
                  <p className="carry-intro">All items listed are essential, unless marked as optional. Items are divided into five categories for your convenience.</p>
                  
                  <div className="carry-categories">
                    <div className="carry-category">
                      <h4><i className="fas fa-hiking"></i> Trekking Gear</h4>
                      <ul>
                        <li>Rucksack with rain cover</li>
                        <li>Day pack bag</li>
                        <li>Head torch with spare batteries</li>
                        <li>UV protection sunglasses</li>
                        <li>Water bottles: Two 1-liter bottles</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-shoe-prints"></i> Footwear</h4>
                      <ul>
                        <li>Non-skid, deep treaded, high-ankle trekking shoes: 1 pair</li>
                        <li>Lightweight slippers or sandals: 1 pair</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-tshirt"></i> Clothing</h4>
                      <ul>
                        <li>Quick-dry warm lower or track pants: 2</li>
                        <li>Full-sleeve T-shirts/sweatshirts: 1 for every 2 days of trekking</li>
                        <li>Thick woolen socks: 1 pair for every 2 days of trekking</li>
                        <li>Thermal body warmer (upper & lower): 1 set</li>
                        <li>Undergarments: 1 for each day of trekking</li>
                        <li>Warm jacket (closed at wrist & neck): 1</li>
                        <li>Full-sleeve sweater: 1</li>
                        <li>Rainwear (jacket & pants): 1 set</li>
                        <li>Waterproof, warm gloves: 1 pair</li>
                        <li>Woolen cap: 1</li>
                        <li>Sun-shielding hat: 1</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-soap"></i> Toiletries</h4>
                      <ul>
                        <li>Personal toiletries kit: Small towel, toilet paper, paper soap, bar soap, toothbrush, toothpaste, cold cream, etc.</li>
                        <li>Sunscreen lotion: Small pack</li>
                        <li>Lip balm: Small pack</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-utensils"></i> Utensils</h4>
                      <ul>
                        <li>Small, lightweight, leak-proof lunch box: 1</li>
                        <li>Plate: 1</li>
                        <li>Spoon: 1</li>
                        <li>Tea/coffee mug (plastic): 1</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-plus-circle"></i> Miscellaneous</h4>
                      <ul>
                        <li className="optional">Camera: (Optional)</li>
                        <li>Personal medicines: Bring plenty if needed; consult your doctor before the trek.</li>
                        <li className="optional">Dry fruits, nuts, chocolate bars (Optional)</li>
                      </ul>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li>Pick and Drop Transfers from/to Rishikesh</li>
                    <li>All meals are included with proper hygiene parameters</li>
                    <li>Triple/double sharing tent accommodations are available</li>
                    <li>During the trek, there will be morning and evening tea with a small refreshment</li>
                    <li>All tolls and forest fees</li>
                    <li>Trekking gears (tents, sleeping bags, ropes, gaiters, crampons etc.)</li>
                    <li>First Aid Medical Kit</li>
                    <li>Expert Guide</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li>Food during transit</li>
                    <li>Any kind of personal expenses</li>
                    <li>Any kind of personal medication</li>
                    <li>Transportation to and from Rishikesh</li>
                    <li>Personal trekking gear and equipment</li>
                    <li>Any additional expenses due to weather conditions or unforeseen circumstances</li>
                    <li>Anything that has not been covered in the inclusions mentioned above</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">{tourData.price}</span>
                    <span className="duration">{tourData.duration}</span>
                  </div>

                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>{tourData.region}</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: {tourData.bestTime}</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: {tourData.difficulty}</span>
                    </div>
                    <div className="feature">
                      <FaMountain />
                      <span>Altitude: {tourData.altitude}</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="quick-info-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Quick Info</h3>
                  <div className="info-item">
                    <FaMountain />
                    <div>
                      <strong>Max Altitude:</strong>
                      <span>12,100 feet</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaMapMarkerAlt />
                    <div>
                      <strong>Trek Distance:</strong>
                      <span>24 km (round trip)</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaStar />
                    <div>
                      <strong>Difficulty Level:</strong>
                      <span>Moderate</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaCalendarAlt />
                    <div>
                      <strong>Best Season:</strong>
                      <span>December to March</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="cost-details-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Cost Details</h3>
                  <h4>Cost Per Person (Ex. Rishikesh)</h4>
                  <ul>
                    <li><strong>Quad Sharing:</strong> ₹ 5,999 + 5% GST</li>
                    <li><strong>Triple Sharing:</strong> ₹ 6,499 + 5% GST</li>
                    <li><strong>Double Sharing:</strong> ₹ 6,999 + 5% GST</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="pickup-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Pickup Points</h3>
                  <ul>
                    <li><strong>Rishikesh:</strong> Rishikesh bus stand or railway station between 07:00 AM to 08:00 AM</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="tour-highlights-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Tour Highlights</h3>
                  <ul>
                    <li>Winter trek with snow-covered trails</li>
                    <li>Stunning mountain views</li>
                    <li>Beautiful alpine meadows</li>
                    <li>Moderate difficulty level</li>
                    <li>Perfect for winter adventure</li>
                    <li>Photography opportunities</li>
                    <li>Rhododendron forests</li>
                    <li>High-altitude camping</li>
                    <li>Expert guide and support</li>
                  </ul>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName={tourData.name}
                  tourPrice={tourData.price}
                  tourDuration={tourData.duration}
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default Brahmatal;
